import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
// import { BENCHMARK_UNIVERSES } from '../../config/constants/esgBenchmarkData';
import { useUniverses } from '@biasafe.queries/securities-universes-data/securities-universes-data';
import type { Universe } from '../../types';
import { STATIC_UNIVERSES } from '../../config/constants/staticUniverses';
import { useSmartBetaStrategyStore } from '@/features/Strategies/stores/definitionFormStore';
import { universeCatalogueQuery } from '@/shared/config/queryDefaults';
import { useUniverseAsOfDate } from '../../../RebalancingSchedule/hooks/useLatestReconstitutionDate';

export function useBenchmarkSelection(universeId: string) {
  // Step 2's latest reconstitution date. The universe is resolved AT that
  // date so the eligible set and the portfolio it will rebalance are counted
  // at the same instant; it is also part of the queryKey, so changing the
  // schedule refetches instead of serving the previous date from cache.
  const asOf = useUniverseAsOfDate();
  const { data: chUniverses } = useUniverses(
    asOf ? { as_of: asOf } : undefined,
    {
      query: universeCatalogueQuery(asOf),
    },
  );
  // Benchmarks are drawn from the universe list itself: the locked default is
  // the selected universe, and any of the other indices can be added. Fall back
  // to the static catalog when ClickHouse is unavailable so the default chip
  // and the add-list still resolve. Mirrors usePointInTime / useClassificationData.
  const universes = chUniverses?.length ? chUniverses : STATIC_UNIVERSES;
  const { selectedBenchmarks, setBenchmark } = useSmartBetaStrategyStore(
    useShallow((state) => ({
      selectedBenchmarks: state.data_processor.benchmark,
      setBenchmark: state.setBenchmark,
    })),
  );

  // Keep the selected universe present as the locked default benchmark WITHOUT
  // wiping the rest. This used to `setBenchmark([universeId])` on every mount,
  // which discarded a rehydrated draft's user-added benchmarks before the user
  // ever saw them. Seed the default only when it's missing, reading fresh store
  // state so a universe switch keeps the extras too.
  // Keep the selected universe as the locked default benchmark WITHOUT wiping
  // user-added extras. This used to `setBenchmark([universeId])` on every mount,
  // which discarded a rehydrated draft's extra benchmarks before the user ever
  // saw them. Now:
  //  - on a universe CHANGE, swap the outgoing universe-default for the new one
  //    but keep everything else, and
  //  - on mount / same universe, only seed the default when it's missing.
  const prevUniverseRef = useRef(universeId);
  useEffect(() => {
    const prev = prevUniverseRef.current;
    if (prev !== universeId) {
      prevUniverseRef.current = universeId;
      setBenchmark([
        universeId,
        ...selectedBenchmarks.filter((b) => b !== universeId && b !== prev),
      ]);
      return;
    }
    if (!selectedBenchmarks.includes(universeId)) {
      setBenchmark([universeId, ...selectedBenchmarks]);
    }
  }, [universeId, selectedBenchmarks, setBenchmark]);

  const availableBenchmarks = useMemo(
    () => universes.filter((u: Universe) => !selectedBenchmarks.includes(u.id)),
    [selectedBenchmarks, universes],
  );

  const selectedBenchmarkDetails = useMemo(
    () => universes.filter((u: Universe) => selectedBenchmarks.includes(u.id)),
    [selectedBenchmarks, universes],
  );

  const addBenchmark = useCallback(
    (id: string) => setBenchmark([...selectedBenchmarks, id]),
    [selectedBenchmarks, setBenchmark],
  );

  const removeBenchmark = useCallback(
    (id: string) => {
      if (id === universeId) return;
      setBenchmark(selectedBenchmarks.filter((s: string) => s !== id));
    },
    [universeId, selectedBenchmarks, setBenchmark],
  );

  return {
    selectedBenchmarks,
    availableBenchmarks,
    selectedBenchmarkDetails,
    defaultBenchmarkId: universeId,
    addBenchmark,
    removeBenchmark,
  };
}
